import { Check } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * A linear multi-step form.
 *
 * Built for flows where a wrong field is cheap to enter and expensive to discover.
 * Creating a challenge is the motivating case: a `course_tag` that does not match the
 * client build, or a maze that is not byte-identical to the one students download, is
 * accepted happily and then makes *every* submission fail days later — with an error
 * the students see and the TA does not. A step with room to explain that beats one
 * long form where the explanation is a line of grey text among eleven fields.
 */

export interface Step {
  id: string;
  title: string;
  /** Shown under the title on the active step. */
  hint?: string;
  /**
   * What is still missing, in the user's words. Empty means complete.
   *
   * Reasons rather than a boolean, deliberately. A disabled Next button with no
   * explanation is its own bug: it leaves the user comparing the form against their
   * memory of what it asked for. `StepperFooter` lists these beside the button.
   */
  incomplete: string[];
}

export function useStepper(steps: Step[]) {
  const [requested, setRequested] = useState(0);

  /**
   * The furthest reachable step: the first incomplete one.
   *
   * Derived on every render rather than remembered. Editing an earlier step back
   * into an invalid state therefore also revokes the later ones, instead of leaving
   * a form that is half-filled but still submittable.
   */
  const firstIncomplete = steps.findIndex((s) => s.incomplete.length > 0);
  const furthest = firstIncomplete === -1 ? steps.length - 1 : firstIncomplete;
  const index = Math.min(requested, furthest);
  const blocked = steps[index].incomplete;

  return {
    steps,
    index,
    current: steps[index],
    blocked,
    isFirst: index === 0,
    isLast: index === steps.length - 1,
    canAdvance: blocked.length === 0,
    canJumpTo: (target: number) => target <= furthest,
    goTo: (target: number) => setRequested(Math.max(0, target)),
    next: () => setRequested(Math.min(index + 1, steps.length - 1)),
    back: () => setRequested(Math.max(index - 1, 0)),
    reset: () => setRequested(0),
  };
}

export type Stepper = ReturnType<typeof useStepper>;

/**
 * The steps, across the top or down the side.
 *
 * Vertical suits a dialog with a wide body -- the rubric editor, a maze picker -- where a
 * row of five titles would fight the content for the top of the box; on a phone it folds
 * back to a row, because there is no side to put it on.
 */
export function StepperNav({
  stepper,
  orientation = "horizontal",
}: {
  stepper: Stepper;
  orientation?: "horizontal" | "vertical";
}) {
  const { steps, index, canJumpTo, goTo } = stepper;
  const vertical = orientation === "vertical";
  return (
    <ol className={cn("flex items-center gap-1", vertical && "sm:flex-col sm:items-stretch sm:gap-0.5")}>
      {steps.map((step, i) => {
        const done = i < index && step.incomplete.length === 0;
        const active = i === index;
        const reachable = canJumpTo(i);
        return (
          <li key={step.id} className={cn("flex min-w-0 flex-1 items-center gap-1", vertical && "sm:flex-none")}>
            <button
              type="button"
              disabled={!reachable}
              onClick={() => goTo(i)}
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                vertical && "sm:py-2 sm:text-sm",
                active && "bg-muted font-medium",
                reachable && !active && "hover:bg-muted/60",
                !reachable && "cursor-not-allowed opacity-50",
              )}
            >
              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full border text-[10px]",
                  active && "border-primary text-primary",
                  done && "border-primary bg-primary text-primary-foreground",
                )}
                aria-hidden
              >
                {done ? <Check className="size-3" /> : i + 1}
              </span>
              <span className="truncate">{step.title}</span>
            </button>
            {i < steps.length - 1 ? (
              <span className={cn("bg-border h-px w-2 shrink-0", vertical && "sm:hidden")} aria-hidden />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

export function StepperFooter({
  stepper,
  onFinish,
  finishLabel,
  busy,
}: {
  stepper: Stepper;
  onFinish: () => void;
  finishLabel: string;
  busy?: boolean;
}) {
  const { isFirst, isLast, canAdvance, blocked, back, next } = stepper;
  return (
    <div className="flex w-full items-center justify-between gap-4">
      {/* Say why, not just that it is disabled. */}
      <p className="text-muted-foreground min-w-0 flex-1 text-xs">
        {blocked.length > 0 ? `Still needed: ${blocked.join(", ")}` : null}
      </p>
      <div className="flex shrink-0 gap-2">
        <Button variant="outline" onClick={back} disabled={isFirst || busy}>
          Back
        </Button>
        {isLast ? (
          <Button onClick={onFinish} disabled={!canAdvance || busy}>
            {finishLabel}
          </Button>
        ) : (
          <Button onClick={next} disabled={!canAdvance}>
            Next
          </Button>
        )}
      </div>
    </div>
  );
}
